// React wrapper around the lane storage model (lanes.ts) and its read model
// (composite.ts).
//
// WHY THE LANES ARE BUILT DURING RENDER AND NOT IN AN EFFECT. A source reports
// slices from an effect of its own, and in React a CHILD's effects run before
// its parent's. Building the lanes in this hook's effect therefore lost the
// source's very first burst of results: they arrived while `setRef.current`
// was still null, `report` dropped them, and a source never re-reports a slice
// it has already emitted — so they were gone for good. The visible symptom was
// a counter that ticked up correctly but sat permanently one lookahead behind
// the truth, which looked like a slow chart rather than a lost one.
//
// Deriving the LaneSet during render fixes it at the root: by the time any
// child effect can call `report`, the lanes exist. This is React's documented
// "adjust state when a prop changes" pattern — the setState call below happens
// during render of this same component, so React re-renders immediately and
// nothing downstream ever observes the stale value.
//
// Wiring only: no policy decisions live here. See lanes.ts and composite.ts
// for the actual write/read models.
import { useCallback, useMemo, useRef, useState } from 'react'
import { composite, type CompositeSummary } from './composite.js'
import { applyResult, buildLanes, type LaneSet } from './lanes.js'
import type { SegmentVerificationResult } from '../SegmentVerifier.js'
import type { RenditionVerified } from '../verifyRendition.js'
import type { RootVerified } from '../verifyRoot.js'

export interface LedgerHandle {
  set: LaneSet | null
  summary: CompositeSummary | null
  report: (result: SegmentVerificationResult) => void
}

export function useVerificationLedger(
  root: RootVerified | null,
  renditions: RenditionVerified[],
): LedgerHandle {
  const [set, setSet] = useState<LaneSet | null>(null)
  const setRef = useRef<LaneSet | null>(null)
  // Which video the current lanes belong to. Rebuilding is keyed on this so
  // navigating between versions starts from scratch rather than carrying a
  // previous video's coverage over — and so a root that is merely a new object
  // for the same video does not wipe the evidence.
  const builtFor = useRef<string | null>(null)

  const videoId = root?.manifest.video_id ?? null

  if (videoId !== builtFor.current) {
    builtFor.current = videoId
    const next = root ? buildLanes(root, renditions) : null
    setRef.current = next
    setSet(next)
  }

  const report = useCallback((result: SegmentVerificationResult) => {
    const current = setRef.current
    if (!current) return
    const next = applyResult(current, result)
    if (next === current) return
    setRef.current = next
    setSet(next)
  }, [])

  const summary = useMemo(() => (set ? composite(set) : null), [set])

  return { set, summary, report }
}
